import React from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from "../../components/ui/drawer";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { X, Download, FileText, Calendar, User } from "lucide-react";
import { ExecutedDocument } from "../../types/schema";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: ExecutedDocument | undefined;
}

const documentTypeLabels: Record<string, string> = {
  rental_schedule: "Rental Schedule",
  power_of_attorney: "Power of Attorney",
  ownership_acknowledgement: "Ownership Acknowledgement"
};

export default function ScheduleDocumentDrawer({ open, onOpenChange, document }: Props) {
  const handleDownload = () => {
    if (document?.fileUrl) {
    }
  };

  if (!document) return null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[90vh]">
        <DrawerHeader className="border-b">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <DrawerTitle>Executed {documentTypeLabels[document.documentType] || document.documentType}</DrawerTitle>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />
                  <span>{document.fileName}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>Uploaded {new Date(document.uploadedDate!).toLocaleDateString('en-IN')}</span>
                </div>
                {document.uploadedBy && (
                  <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    <span>{document.uploadedBy}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                Version {document.version || 1}
              </Badge>
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <DrawerClose asChild>
                <Button variant="ghost" size="icon">
                  <X className="h-4 w-4" />
                </Button>
              </DrawerClose>
            </div>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-auto bg-muted/30">
          <div className="max-w-5xl mx-auto p-6">
            {document.fileName?.toLowerCase().endsWith('.pdf') ? (
              <div className="bg-background border rounded-lg shadow-sm aspect-[8.5/11] flex items-center justify-center">
                <div className="text-center space-y-3">
                  <FileText className="h-16 w-16 mx-auto text-muted-foreground" />
                  <div>
                    <p className="font-medium">PDF Document</p>
                    <p className="text-sm text-muted-foreground mt-1">{document.fileName}</p>
                  </div>
                  <Button variant="outline" onClick={handleDownload}>
                    <Download className="h-4 w-4 mr-2" />
                    Download to View
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-background border rounded-lg shadow-sm overflow-hidden">
                <img 
                  src={document.fileUrl} 
                  alt={document.fileName}
                  className="w-full h-auto"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent) {
                      parent.innerHTML = `
                        <div class="aspect-[8.5/11] flex items-center justify-center">
                          <div class="text-center space-y-3">
                            <div class="text-muted-foreground">
                              <svg class="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <p class="font-medium">Unable to preview</p>
                            <p class="text-sm text-muted-foreground">${document.fileName}</p>
                          </div>
                        </div>
                      `;
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}